/**
 * Demo requests from standingdocs.com: who asked, from where, and what
 * has been done about it. New ones lead. "Contacted" and "Dismissed" are
 * the only two states after that; converting one into an organization is
 * the New organization form, with the details copied across.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';

export interface DemoRequest {
  id: string;
  orgName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  /** Asked until 2026-09-21; older requests still carry it. */
  estimatedOrgSize: string | null;
  /** '1', '2-5', '6-15', '16+' or 'not-sure'. */
  locationCount?: string | null;
  message: string | null;
  status: 'new' | 'contacted' | 'dismissed';
  convertedOrgId: string | null;
  createdAt: string | null;
}

/** "2-5" as a person reads it. */
export function locationsText(count: string): string {
  if (count === 'not-sure') return 'Locations: not sure';
  if (count === '1') return '1 location';
  return `${count.replace('-', ' to ')} locations`;
}

type Filter = 'new' | 'contacted' | 'dismissed' | 'all';

function when(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function RequestsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('new');
  const list = useQuery({
    queryKey: ['demo-requests', filter],
    retry: false,
    queryFn: () => call<{ status?: string }, { requests: DemoRequest[] }>('sysAdminListOnboardingRequests', filter === 'all' ? {} : { status: filter }),
  });
  const update = useMutation({
    mutationFn: (input: { requestId: string; status: 'contacted' | 'dismissed' }) =>
      call<{ requestId: string; status: string }, unknown>('sysAdminUpdateOnboardingRequest', input),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['demo-requests'] }); },
  });

  return (
    <>
      <h1 className={own.title}>Demo requests</h1>
      <p className={own.subtitle}>
        From the form at standingdocs.com. Each one was also emailed to every system administrator when it arrived.
        Reply to the person, then mark it contacted; when it becomes a customer, create the organization with these details.
      </p>

      <div role="group" aria-label="Show" style={{ display: 'flex', gap: 'var(--sp-2)', marginBottom: 'var(--sp-4)', flexWrap: 'wrap' }}>
        {(['new', 'contacted', 'dismissed', 'all'] as Filter[]).map((f) => (
          <button key={f} type="button" className={`${page.btn} ${filter === f ? page.btnPrimary : ''}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f[0]!.toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {update.error && (
        <div className={own.error} role="alert">{isCallFailure(update.error) ? update.error.message : 'That change could not be made.'}</div>
      )}

      {list.isLoading ? <Spinner label="Loading requests" /> : list.error ? (
        <PageState title="Could not load requests" tone="alert">
          {isCallFailure(list.error) ? list.error.message : 'The list did not come back. Reload the page to try again.'}
        </PageState>
      ) : (list.data?.requests.length ?? 0) === 0 ? (
        <div className={own.panel}>
          <PageState title={filter === 'new' ? 'No new requests' : filter === 'all' ? 'No demo requests yet' : `Nothing marked ${filter}`}>
            {filter === 'contacted' || filter === 'dismissed'
              ? 'Requests arrive under New and move here when you mark them.'
              : 'Demo requests from the form on standingdocs.com land here, and each one is emailed to you as well.'}
          </PageState>
        </div>
      ) : (
        <ul className={page.list} aria-label="Demo requests" style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          {list.data!.requests.map((r) => (
            <li key={r.id} className={own.panel} style={{ padding: 'var(--sp-5)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
                <div>
                  <h2 className={page.sectionTitle} style={{ margin: 0 }}>{r.orgName}</h2>
                  <p className={page.itemMeta} style={{ marginTop: 4 }}>
                    {r.contactName} · <a href={`mailto:${r.contactEmail}?subject=${encodeURIComponent(`StandingDocs demo for ${r.orgName}`)}`}>{r.contactEmail}</a>
                    {r.contactPhone && <> · {r.contactPhone}</>}
                    {r.locationCount && <> · {locationsText(r.locationCount)}</>}
                    {r.estimatedOrgSize && <> · {r.estimatedOrgSize}</>}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className={`${own.pill} ${r.status === 'new' ? own.pillDraft : r.status === 'contacted' ? own.pillActive : own.pillOther}`}>{r.status}</span>
                  <span className={`${page.itemMeta} tnum`}>{when(r.createdAt)}</span>
                </div>
              </div>
              {r.message && <p style={{ marginTop: 'var(--sp-3)', whiteSpace: 'pre-wrap', maxWidth: '70ch' }}>{r.message}</p>}
              <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                {r.status !== 'contacted' && (
                  <button type="button" className={page.btn} disabled={update.isPending} onClick={() => update.mutate({ requestId: r.id, status: 'contacted' })}>Mark contacted</button>
                )}
                {r.status !== 'dismissed' && (
                  <button type="button" className={page.btn} disabled={update.isPending} onClick={() => update.mutate({ requestId: r.id, status: 'dismissed' })}>Dismiss</button>
                )}
                {r.convertedOrgId ? (
                  <Link to={`/console/orgs/${r.convertedOrgId}`} className={page.btn}>Open the organization</Link>
                ) : (
                  <Link to={`/console/orgs/new?name=${encodeURIComponent(r.orgName)}&sysadmin=${encodeURIComponent(r.contactEmail)}`} className={`${page.btn} ${page.btnPrimary}`}>Create the organization</Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
